import { useEffect, useState } from "react";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useAsync, Table, Loading, ErrorBox, Empty, Field, Modal, Chip, fmtDate, useAction } from "./AdminUI";

const UserActions = ({ user, onDone }) => {
  const { busy, err, run } = useAction();
  const { user: me } = useAuth();
  const [reason, setReason] = useState("");
  const needsReason = ["blocked", "suspended"].includes(user.status);
  const isSelf = !!(me && user.id === me.id);
  const act = (status) =>
    run(
      () => api.post(`/admin/users/${user.id}/status`, { status, reason }),
      () => { toast.success(`User ${status}.`); onDone(); },
    );

  if (isSelf) {
    return (
      <p className="ap-note" data-testid="ap-user-self-guard">
        This is your own account — its status cannot be changed here. Another Super Admin must do that if ever needed.
      </p>
    );
  }

  return (
    <div>
      {err && <ErrorBox error={err} />}
      {(user.status === "blocked" || user.status === "suspended" || user.status === "deactivated") ? (
        <button type="button" className="btn btn-solid btn-sm" data-testid="ap-user-restore" disabled={busy} onClick={() => act("active")}>
          Restore
        </button>
      ) : (
        <div className="ap-row">
          <Field label="Reason (required to block / suspend)">
            <input className="ap-input" data-testid="ap-user-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for the record" />
          </Field>
        </div>
      )}
      <div className="ap-table-actions" style={{ marginTop: 10 }}>
        {user.status === "active" && (
          <>
            <button type="button" className="btn btn-outline btn-sm" data-testid="ap-user-suspend" disabled={busy || (needsReason && !reason)} onClick={() => act("suspended")}>Suspend</button>
            <button type="button" className="btn btn-outline btn-sm" data-testid="ap-user-block" disabled={busy || !reason} onClick={() => act("blocked")}>Block</button>
            <button type="button" className="btn btn-outline btn-sm" data-testid="ap-user-deactivate" disabled={busy} onClick={() => act("deactivated")}>Deactivate</button>
          </>
        )}
      </div>
    </div>
  );
};

const UsersTable = ({ users, onView }) => (
  <Table columns={["Name", "Email", "Mobile", "Role", "Status", "Profile", "Registered", "Last Login", ""]}>
    {users.map((u) => (
      <tr key={u.id}>
        <td>{u.fullName || "—"}</td>
        <td>{u.email}</td>
        <td>{u.mobile || "—"}</td>
        <td>{u.role}</td>
        <td><Chip value={u.status} /></td>
        <td>{u.profileId || "—"}</td>
        <td>{fmtDate(u.createdAt)}</td>
        <td>{fmtDate(u.lastLoginAt)}</td>
        <td><button type="button" className="btn btn-outline btn-sm" data-testid={`ap-user-view-${u.email}`} onClick={() => onView(u)}>View</button></td>
      </tr>
    ))}
  </Table>
);

export const AdminUsers = () => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const { data, loading, error, reload } = useAsync(
    () => api.get(`/admin/users?search=${encodeURIComponent(search)}&status=${status}`),
    [search, status],
  );
  const [viewUser, setViewUser] = useState(null);

  return (
    <>
      <div className="ap-card">
        <h3 className="ap-card-title">User Management</h3>
        <div className="ap-filters">
          <input className="ap-input" data-testid="ap-users-search" placeholder="Search name, email or mobile…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <select className="ap-select" data-testid="ap-users-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
            <option value="suspended">Suspended</option>
            <option value="deactivated">Deactivated</option>
          </select>
        </div>
        {loading ? <Loading /> : error ? <ErrorBox error={error} /> : data.users.length === 0 ? <Empty /> : (
          <UsersTable users={data.users} onView={setViewUser} />
        )}
      </div>
      {viewUser && (
        <UserDetailModal userId={viewUser.id} onClose={() => { setViewUser(null); reload(); }} />
      )}
    </>
  );
};

const UserDetailModal = ({ userId, onClose }) => {
  const { data, loading, error, reload } = useAsync(() => api.get(`/admin/users/${userId}`), [userId]);
  return (
    <Modal title="User Details" onClose={onClose} wide>
      {loading ? <Loading /> : error ? <ErrorBox error={error} /> : (
        <>
          <dl className="ap-kv">
            <dt>Name</dt><dd>{data.user.fullName || "—"}</dd>
            <dt>Email</dt><dd>{data.user.email}</dd>
            <dt>Mobile</dt><dd>{data.user.mobile || "—"}</dd>
            <dt>Registered for</dt><dd>{data.user.registeredFor || "—"}</dd>
            <dt>Status</dt><dd><Chip value={data.user.status} /></dd>
            <dt>Created</dt><dd>{fmtDate(data.user.createdAt)}</dd>
            <dt>Last login</dt><dd>{fmtDate(data.user.lastLoginAt)}</dd>
          </dl>
          {data.profile && (
            <p className="ap-note" style={{ margin: "12px 0" }}>
              Linked profile: <strong>{data.profile.profileId}</strong> ({data.profile.approved ? "Approved/Live" : data.profile.status})
            </p>
          )}
          <UserActions user={data.user} onDone={reload} />
        </>
      )}
    </Modal>
  );
};

export const AdminBlocked = () => {
  const { data, loading, error, reload } = useAsync(() => api.get("/admin/users"), []);
  const [viewUser, setViewUser] = useState(null);
  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  const flagged = data.users.filter((u) => ["blocked", "suspended", "deactivated"].includes(u.status));
  return (
    <>
      <div className="ap-card">
        <h3 className="ap-card-title">Blocked / Suspended Users</h3>
        {flagged.length === 0 ? <Empty>No blocked, suspended or deactivated accounts.</Empty> : (
          <UsersTable users={flagged} onView={setViewUser} />
        )}
      </div>
      {viewUser && <UserDetailModal userId={viewUser.id} onClose={() => { setViewUser(null); reload(); }} />}
    </>
  );
};

export const ChangePasswordCard = () => {
  const { busy, err, run } = useAction();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [localErr, setLocalErr] = useState("");

  const submit = () => {
    setLocalErr("");
    if (!form.current || !form.next || !form.confirm) {
      setLocalErr("Please fill in all three fields.");
      return;
    }
    if (form.next.length < 8) {
      setLocalErr("New password must be at least 8 characters.");
      return;
    }
    if (form.next !== form.confirm) {
      setLocalErr("New password and confirmation do not match.");
      return;
    }
    if (form.next === form.current) {
      setLocalErr("Please choose a password different from the current one.");
      return;
    }
    run(
      () => api.post("/admin/change-password", {
        current_password: form.current,
        new_password: form.next,
      }),
      () => {
        toast.success("Password updated successfully. Use it at your next login.");
        setForm({ current: "", next: "", confirm: "" });
      },
    );
  };

  const pwField = (key, testId, placeholder) => (
    <div className="ap-field">
      <label className="ap-label">{placeholder}</label>
      <div style={{ position: "relative" }}>
        <input
          type={show ? "text" : "password"}
          className="ap-input"
          data-testid={testId}
          value={form[key]}
          placeholder={placeholder}
          autoComplete="new-password"
          style={{ paddingRight: 40 }}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        />
        <button
          type="button"
          className="auth-eye"
          data-testid={`${testId}-toggle`}
          aria-label={show ? "Hide password" : "Show password"}
          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer" }}
          onClick={() => setShow((v) => !v)}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="ap-card" data-testid="ap-change-password">
      <h3 className="ap-card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <KeyRound size={18} aria-hidden="true" /> Change Admin Password
      </h3>
      {err && <ErrorBox error={err} />}
      {localErr && <ErrorBox error={localErr} />}
      <div className="ap-row">
        {pwField("current", "ap-changepw-current", "Current password")}
        {pwField("next", "ap-changepw-new", "New password (min 8 characters)")}
        {pwField("confirm", "ap-changepw-confirm", "Confirm new password")}
      </div>
      <p className="ap-note" style={{ marginBottom: 12 }}>
        The new password is stored securely (hashed) in the existing authentication system and
        stays in force even after a backend restart. Password hashes are never displayed.
        If the password is ever forgotten, it can be reset from the backend environment.
      </p>
      <button
        type="button"
        className="btn btn-solid btn-sm"
        data-testid="ap-changepw-submit"
        disabled={busy}
        onClick={submit}
      >
        {busy ? "Updating…" : "Update Password"}
      </button>
    </div>
  );
};

export const TestCleanupCard = () => {
  const { busy, err, run } = useAction();
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [counts, setCounts] = useState(null);

  const loadPreview = () =>
    run(() => api.get("/admin/cleanup/preview"), (r) => { setPreview(r); setCounts(null); setConfirm(""); });

  const execute = () =>
    run(
      () => api.post("/admin/cleanup", { confirm }),
      (r) => { setCounts(r.deleted); setPreview(null); setConfirm(""); toast.success("Test data cleanup complete."); },
    );

  const c = preview?.preview?.counts;
  const total = c ? Object.values(c).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="ap-card" data-testid="ap-test-cleanup">
      <h3 className="ap-card-title">Test Data Cleanup (Pre-Launch)</h3>
      <p className="ap-note" style={{ marginBottom: 14 }}>
        Safely identifies and removes ONLY development/test records (accounts like
        @suffatest.com / @test.com, "Test Bride/Test Groom", E2E discussions/reports) together with
        their linked profiles, payments, interests, matches, meetings, verifications and reports.
        Real user data and the 12 seeded browse profiles are never touched. Preview first, then
        confirm by typing DELETE.
      </p>
      {err && <ErrorBox error={err} />}
      {counts && (
        <div className="ap-msg ap-msg-admin" data-testid="ap-cleanup-result">
          <strong>Cleanup complete:</strong>{" "}
          {Object.entries(counts).filter(([, v]) => v > 0).map(([k, v]) => `${k}: ${v}`).join(" · ") || "nothing to remove"}
        </div>
      )}
      {preview && (
        <div className="ap-msg" data-testid="ap-cleanup-preview">
          <div className="ap-msg-meta">Preview — only these records will be removed:</div>
          {total === 0 ? (
            <strong>No development/test records found. The database is clean.</strong>
          ) : (
            <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 13 }}>
              {Object.entries(c).filter(([, v]) => v > 0).map(([k, v]) => <li key={k}>{k}: {v}</li>)}
            </ul>
          )}
          {preview.preview.users.slice(0, 6).map((u) => (
            <div key={u.email} className="ap-note" style={{ marginTop: 4 }}>{u.name || "—"} · {u.email}</div>
          ))}
          {preview.preview.discussions.slice(0, 4).map((t) => (
            <div key={t} className="ap-note" style={{ marginTop: 4 }}>Discussion: {t}</div>
          ))}
        </div>
      )}
      <div className="ap-filters" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-outline btn-sm" data-testid="ap-cleanup-preview-btn" disabled={busy} onClick={loadPreview}>
          {busy ? "Scanning…" : "Preview Test Records"}
        </button>
        {preview && total > 0 && (
          <>
            <input
              className="ap-input" style={{ width: 160 }} placeholder="Type DELETE"
              data-testid="ap-cleanup-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            />
            <button type="button" className="btn btn-solid btn-sm" data-testid="ap-cleanup-execute" disabled={busy || confirm !== "DELETE"} onClick={execute}>
              Delete Test Records
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const UpiSettingsCard = () => {
  const { data, loading, error } = useAsync(() => api.get("/admin/upi-settings"), []);
  const { busy, err, run } = useAction();
  const [upiId, setUpiId] = useState("");
  const [payeeName, setPayeeName] = useState("");
  const [qrImage, setQrImage] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (data && !loaded) {
      setUpiId(data.upiId || "");
      setPayeeName(data.payeeName || "");
      setQrImage(data.qrImage || "");
      setLoaded(true);
    }
  }, [data, loaded]);

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      toast.error("Please choose a PNG, JPG or WEBP image.");
      return;
    }
    if (file.size > 1_100_000) {
      toast.error("Image too large — please use a QR image under 1 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setQrImage(String(reader.result));
    reader.readAsDataURL(file);
  };

  return (
    <div className="ap-card" data-testid="ap-upi-settings">
      <h3 className="ap-card-title">UPI Payment Settings (₹500 Registration Fee)</h3>
      <p className="ap-note" style={{ marginBottom: 12 }}>
        Members see this QR on the payment page, pay ₹500 by UPI, and submit the transaction reference (UTR).
        You then verify it under Registration Payments → Pending Verification. Upload the REAL QR from your
        UPI app (PNG/JPG, under 1 MB) — no placeholder is shown to members.
      </p>
      {loading ? <Loading /> : error ? <ErrorBox error={error} /> : (
        <>
          <div className="ap-row">
            <Field label="Your UPI ID (e.g. merchant@upi)">
              <input className="ap-input" data-testid="ap-upi-id" value={upiId} onChange={(e) => setUpiId(e.target.value)} />
            </Field>
            <Field label="Account holder name (shown to members)">
              <input className="ap-input" data-testid="ap-upi-payee" value={payeeName} onChange={(e) => setPayeeName(e.target.value)} />
            </Field>
          </div>
          <div className="ap-row">
            <Field label="UPI QR image (PNG/JPG under 1 MB)">
              <input type="file" accept="image/png,image/jpeg,image/webp" data-testid="ap-upi-file" onChange={onFile} />
            </Field>
            {qrImage && (
              <div style={{ textAlign: "center" }}>
                <img src={qrImage} alt="UPI QR preview" data-testid="ap-upi-qr-preview" style={{ width: 120, height: 120, objectFit: "contain", border: "1px solid rgba(28,25,23,0.2)", borderRadius: 8, padding: 4, background: "#fff" }} />
                <div><button type="button" className="btn btn-outline btn-sm" data-testid="ap-upi-clear" onClick={() => setQrImage("")}>Remove QR</button></div>
              </div>
            )}
          </div>
          {err && <ErrorBox error={err} />}
          <button
            type="button" className="btn btn-solid btn-sm" data-testid="ap-upi-save" disabled={busy}
            onClick={() => run(
              () => api.post("/admin/upi-settings", { upiId: upiId.trim(), payeeName: payeeName.trim(), qrImage }),
              () => toast.success("UPI payment settings saved."),
            )}
          >
            {busy ? "Saving…" : "Save UPI Settings"}
          </button>
        </>
      )}
    </div>
  );
};

export const AdminSecurity = () => {
  const { user } = useAuth();
  const { data, loading, error, reload } = useAsync(() => api.get("/admin/admins"), []);
  const audit = useAsync(() => api.get("/admin/audit"), []);
  const { busy, err, run } = useAction();
  const [form, setForm] = useState({ email: "", fullName: "", password: "" });
  const isSuper = user?.role === "super_admin";

  return (
    <>
      <ChangePasswordCard />
      <UpiSettingsCard />
      {isSuper && <TestCleanupCard />}
      <div className="ap-card">
        <h3 className="ap-card-title">Admin Accounts</h3>
        {loading ? <Loading /> : error ? <ErrorBox error={error} /> : (
          <Table columns={["Name", "Email", "Role", "Status", "Created", "Last Login", ""]}>
            {data.admins.map((a) => (
              <tr key={a.id}>
                <td>{a.fullName || "—"}</td>
                <td>{a.email}</td>
                <td><Chip value={a.role === "super_admin" ? "successful_match" : "approved"}>{a.role}</Chip></td>
                <td><Chip value={a.status} /></td>
                <td>{fmtDate(a.createdAt)}</td>
                <td>{fmtDate(a.lastLoginAt)}</td>
                <td>
                  {isSuper && a.email !== user.email && a.role === "admin" && (
                    <button
                      type="button" className="btn btn-outline btn-sm" data-testid={`ap-admin-promote-${a.email}`}
                      style={{ marginRight: 8 }}
                      onClick={() => run(() => api.put(`/admin/admins/${a.id}/role`, { role: "super_admin" }), () => { toast.success("Super Admin granted."); reload(); })}
                    >
                      Make Super Admin
                    </button>
                  )}
                  {isSuper && a.role === "admin" && (
                    <button
                      type="button" className="btn btn-outline btn-sm" data-testid={`ap-admin-remove-${a.email}`}
                      onClick={() => run(() => api.delete(`/admin/admins/${a.id}`), () => { toast.success("Admin access revoked."); reload(); })}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
        {isSuper ? (
          <div style={{ marginTop: 18 }}>
            <h4 className="ap-card-title" style={{ fontSize: 18 }}>Create Admin</h4>
            {err && <ErrorBox error={err} />}
            <div className="ap-row">
              <Field label="Email"><input className="ap-input" data-testid="ap-admin-email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></Field>
              <Field label="Full Name"><input className="ap-input" value={form.fullName} onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))} /></Field>
              <Field label="Password (min 8 chars)"><input type="password" className="ap-input" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} /></Field>
            </div>
            <button
              type="button" className="btn btn-solid btn-sm" data-testid="ap-admin-create" disabled={busy}
              onClick={() => run(() => api.post("/admin/admins", form), () => { toast.success("Admin created."); setForm({ email: "", fullName: "", password: "" }); reload(); })}
            >
              {busy ? "Creating…" : "Create Admin"}
            </button>
          </div>
        ) : (
          <p className="ap-note" style={{ marginTop: 12 }}>Only a Super Admin can manage admin accounts.</p>
        )}
      </div>
      <div className="ap-card">
        <h3 className="ap-card-title">Admin Activity / Audit Log</h3>
        {audit.loading ? <Loading /> : audit.error ? <ErrorBox error={audit.error} /> : audit.data.audit.length === 0 ? <Empty>No admin actions recorded yet.</Empty> : (
          <Table columns={["When", "Admin", "Action", "Target", "Details"]}>
            {audit.data.audit.map((a, i) => (
              <tr key={i}>
                <td>{fmtDate(a.created_at)}</td>
                <td>{a.admin_email}</td>
                <td>{a.action}</td>
                <td>{a.target_collection} {a.target_id}</td>
                <td>{a.details}</td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </>
  );
};
